import { Share, Plus, Download, X, Sparkles } from "lucide-react";
import { useLocation } from "react-router-dom";
import { useInstall } from "@/context/InstallContext";

export default function InstallPrompt() {
  const { visible, dismiss, install, isIos, isStandalone, hasDeferred } = useInstall();
  const location = useLocation();

  // Dedicated /install destination already provides its own install UX — don't double-prompt.
  if (location.pathname === "/install") return null;
  if (isStandalone || !visible) return null;

  return (
    <div
      data-testid="pwa-install-prompt"
      role="dialog"
      aria-live="polite"
      aria-labelledby="pwa-install-title"
      className="fixed z-[100] left-3 right-3 bottom-3 sm:left-auto sm:right-5 sm:bottom-5 sm:max-w-[380px] rounded-2xl bg-white shadow-[0_16px_48px_-12px_rgba(249,115,22,0.35),0_4px_16px_-4px_rgba(0,0,0,0.08)] border border-[#F97316]/15 overflow-hidden"
      style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
    >
      {/* Accent stripe */}
      <div className="h-1 bg-gradient-to-r from-[#F97316] via-[#FB923C] to-[#F97316]" />

      <div className="p-4 flex gap-3 items-start">
        <div className="relative shrink-0">
          <img
            src="/icons/icon-192.png"
            alt="NContro"
            className="w-12 h-12 rounded-xl shadow-sm ring-1 ring-black/5"
          />
          <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-[#F97316] flex items-center justify-center">
            <Sparkles className="w-2.5 h-2.5 text-white" strokeWidth={2.5} />
          </span>
        </div>

        <div className="flex-1 min-w-0 pt-0.5">
          <div
            id="pwa-install-title"
            className="text-[15px] font-semibold text-neutral-900 leading-tight"
          >
            Install NContro
          </div>
          {isIos ? (
            <div className="text-[13px] text-neutral-600 mt-1 leading-snug">
              Tap{" "}
              <span className="inline-flex items-center gap-0.5 font-medium text-neutral-900">
                <Share className="w-3.5 h-3.5" strokeWidth={2.2} />
                Share
              </span>{" "}
              &rsaquo;{" "}
              <span className="inline-flex items-center gap-0.5 font-medium text-neutral-900">
                <Plus className="w-3.5 h-3.5" strokeWidth={2.2} />
                Add to Home Screen
              </span>
              .
            </div>
          ) : (
            <div className="text-[13px] text-neutral-600 mt-1 leading-snug">
              Faster launch, works fullscreen, and lives on your home screen.
            </div>
          )}

          <div className="mt-3 flex items-center gap-2">
            {!isIos && hasDeferred && (
              <button
                onClick={install}
                data-testid="pwa-install-btn"
                className="inline-flex items-center gap-1.5 rounded-full bg-[#F97316] hover:bg-[#EA6A11] active:bg-[#D55F0E] text-white text-[13px] font-semibold px-4 py-2 shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#F97316]/50"
              >
                <Download className="w-3.5 h-3.5" strokeWidth={2.4} />
                Install app
              </button>
            )}
            <button
              onClick={dismiss}
              data-testid="pwa-install-later"
              className="text-[13px] font-medium text-neutral-500 hover:text-neutral-900 px-3 py-2 rounded-full transition-colors"
            >
              Not now
            </button>
          </div>
        </div>

        <button
          onClick={dismiss}
          aria-label="Dismiss install prompt"
          data-testid="pwa-install-dismiss"
          className="shrink-0 -mr-1 -mt-1 p-1.5 rounded-full text-neutral-400 hover:text-neutral-700 hover:bg-neutral-100 transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
